import FetchError from '../FetchError'
import type { ProblemDetail } from '../types/types'

/**
 * Normalizes a non-ok Response into a FetchError, always throwing. Content-
 * type driven so callers get a useful message regardless of whether the
 * backend responded with an RFC 7807 problem+json body, a plain JSON error
 * body, or no body at all.
 *
 * The server-provided text (problem+json `detail` or plain JSON `message`) is
 * kept both on `message` (for diagnostics) and on `detail` (so UI code can
 * decide whether it is safe to show to the user). `response.statusText`
 * (`Internal Server Error`, `Conflict`, …) is only used as a last-resort
 * `message` for logging, never surfaced as user copy.
 */
export async function parseFetchError(response: Response): Promise<never> {
  const parseJsonBody = async (): Promise<unknown> => {
    try {
      return await response.json()
    } catch {
      return undefined
    }
  }

  const contentType = response.headers.get('Content-Type') || ''
  if (contentType.includes('application/problem+json')) {
    const json = await parseJsonBody()
    const problemDetail = json as ProblemDetail
    const detail = problemDetail?.detail?.trim() || undefined
    throw new FetchError(
      detail || response.statusText || 'Fetch error',
      problemDetail?.status || response.status,
      detail
    )
  } else if (contentType.includes('application/json')) {
    const json = (await parseJsonBody()) as { message?: string } | undefined
    const detail = json?.message?.trim() || undefined
    throw new FetchError(
      detail || response.statusText || 'Fetch error',
      response.status,
      detail
    )
  } else {
    throw new FetchError(response.statusText || 'Fetch error', response.status)
  }
}

export interface FetchWithAuthRetryDeps {
  getAccessToken: (forceRefresh?: boolean) => Promise<string>
  fetchImpl?: typeof fetch
}

/**
 * Performs (or replays) an authenticated fetch, retrying once with a
 * force-refreshed token if the server responds 401. A 401 usually means the
 * access token expired or was revoked; forcing a fresh token and retrying
 * once avoids logging users out for a token that merely needed renewing.
 *
 * Mode-agnostic and dependency-injected so it can be unit-tested directly;
 * callers (useAuth) are responsible for any test-mode short-circuiting.
 */
export async function fetchWithAuthRetry(
  request: Request,
  deps: FetchWithAuthRetryDeps,
  response?: Response
): Promise<Response> {
  const fetchImpl = deps.fetchImpl ?? fetch
  const originalRequest = request.clone()
  let finalResponse = response
  if (!finalResponse) {
    const token = await deps.getAccessToken()
    request.headers.set('Authorization', `Bearer ${token}`)
    finalResponse = await fetchImpl(request)
  }

  if (finalResponse.status === 401) {
    try {
      const refreshedToken = await deps.getAccessToken(true)
      const retryRequest = originalRequest.clone()
      retryRequest.headers.set('Authorization', `Bearer ${refreshedToken}`)
      finalResponse = await fetchImpl(retryRequest)
    } catch {
      // Keep the original 401; it is surfaced as a FetchError below.
    }
  }

  return finalResponse
}
